import { createRouter, createWebHashHistory } from "vue-router";
import { cursosdb } from '@/data/cursos/cursos';
import { maestriasdb } from '@/data/maestria/maestrias';
import documentos from '@/data/documentacion/documentos.json';

/**
 * Definición de las rutas de la aplicación.
 * @type {Array<Object>}
 */
const routes = [
  /**
    * Paginas principales del Consejo de Posgrado
    */
  {
    path: "/principal",
    component: () => import("../page/principal/PaginaPrincipalCP.vue"),
  },
  {
    path: "/cursos",
    component: () => import("../page/cursos/PaginaPrincipalCursos.vue"),
  },
  {
    path: "/maestrias",
    component: () => import("../page/maestrias/PaginaPrincipalMaestrias.vue"),
  },
  {
    path: "/documentacion",
    component: () => import("../page/documentacion/PaginaPrincipalDocumentacion.vue"),
  },

   /**
  * Ruta de las páginas dentro del NavBar de cursos: 
  * Historia, Formas de Pago, Noticias, Preguntas Frecuentes y la Lista de Cursos
  */
   {
    path: "/cursos-todos",
    component: () => import("../page/cursos/PaginaCursos.vue"),
  },
  {
    path: "/cursos/historia",
    component: () => import("../page/cursos/historia/PaginaHistoriaCursos.vue"),
  },
  {
    path: "/cursos/noticias",
    component: () => import("../page/cursos/noticias/NoticiasCursos.vue"),
  },
  {
    path: "/cursos/preguntas",
    component: () => import("../page/cursos/preguntas/PreguntasViewCursos.vue"),
  },
  {
    path: "/cursos/formas-pago",
    component: () => import("../page/cursos/pago/FormasPagoCursosPage.vue"),
  },
  /**
   * Rutas para un futuro hacerlo estatico, de la redireccion del filtrado de cursos
   */
  {
    path: "/cursos/civil",
    component: () => import("../page/cursos/lista/PaginaCursosCivil.vue"),
  },
  {
    path: "/cursos/computacion",
    component: () => import("../page/cursos/lista/PaginaCursosComputacion.vue"),
  },
  {
    path: "/cursos/industrial",
    component: () => import("../page/cursos/lista/PaginaCursosIndustrial.vue"),
  },
  {
    path: "/cursos/sistemas",
    component: () => import("../page/cursos/lista/PaginaCursosSistemas.vue"),
  },
  /**
  * Ruta de las páginas dentro del NavBar de maestrías:
  * Historia, Formas de Pago, Noticias, Preguntas Frecuentes y la Lista de Maestrías
  */
  {
    path: "/maestrias-todos",
    component: () => import("../page/maestrias/PaginaMaestrias.vue"),
  },
  {
    path: "/maestrias/historia",
    component: () => import("../page/maestrias/historia/PaginaHistoriaMaestria.vue"),
  },
  {
    path: "/maestrias/noticias",
    component: () => import("../page/maestrias/noticias/NoticiasMaestrias.vue"),
  },
  {
    path: "/maestrias/preguntas",
    component: () => import("../page/maestrias/preguntas/PreguntasViewMaestria.vue"),
  },
  {
    path: "/maestrias/formas-pago",
    component: () => import("../page/maestrias/pago/FormasPagoMaestriaPage.vue"),
  },
  /**
   * Rutas para un futuro hacerlo estatico, de la redireccion del filtrado de maestrias
   */
  {
    path: "/maestrias/civil",
    component: () => import("../page/maestrias/lista/PaginaMaestriasCivil.vue"),
  },
  {
    path: "/maestrias/computacion",
    component: () => import("../page/maestrias/lista/PaginaMaestriasComputacion.vue"),
  },
  {
    path: "/maestrias/industrial",
    component: () => import("../page/maestrias/lista/PaginaMaestriasIndustrial.vue"),
  },
  {
    path: "/maestrias/sistemas",
    component: () => import("../page/maestrias/lista/PaginaMaestriasSistemas.vue"),
  },
  
  /**
  * Rutas dinámicas de para cada Procedimiento de la página de documentación.
  */
  {
    path: "/documento/:category/:url",
    name: "DocumentoDetalle",
    component: () => import("../page/documentacion/PaginaProcedimiento.vue"),
  },

  /**
  * Ruta para redireccionamiento de todas las páginas del Consejo de Posgrado y control de una ruta mal escrita.
  */
  {
    path: '',
    component: () => import("../page/not found/NotFoundPage.vue"),
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import("../page/not found/NotFoundPage.vue"),
  },
  {
    path: "/seguimientoGraduados",
    component: () => import("../page/seguimientoGraduados/Seguimiento.vue"),
  }
];

/**
 * Añade rutas dinámicas para los cursos.
 * @param {Array<Object>} cursosdb - Lista de cursos.
 */
cursosdb.forEach(curso => {
  routes.push({
    path: curso.url,
    component: () => import("../page/cursos/CursoGenericoPage.vue"),
    props: { curso }
  });
});

/**
 * Añade rutas dinámicas para las maestrías.
 * @param {Array<Object>} maestriasdb - Lista de maestrías.
 */
maestriasdb.forEach(maestria => {
  routes.push({
    path: maestria.url,
    component: () => import("../page/maestrias/MaestriaGenericaPage.vue"), // Asegúrate de tener un componente genérico para maestrías
    props: { maestria }
  });
});

/**
 * Añade rutas dinámicas para la documentación.
 * @param {Object} documentos - Objeto que contiene las categorías de documentación.
 */
Object.values(documentos).forEach(categoria => {
  categoria.forEach(documentacion => {
    routes.push({
      path: documentacion.url, // Usar la URL proporcionada en el JSON
      component: () => import("../page/documentacion/PaginaProcedimiento.vue"),
      props: route => ({ documentacion, archivo: route.query.url })
    });
  });
});

/**
 * Crea una instancia del router de Vue.
 * @returns {Object} - La instancia del router de Vue.
 */
const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Si hay una posición guardada (por ejemplo, al usar el botón de retroceso), úsala
    if (savedPosition) {
      return savedPosition;
    }
    // De lo contrario, desplázate al inicio de la página
    return { top: 0, left: 0, behavior: 'smooth' };
  },
});
export default router;